import { useState } from 'react';

import { DEFAULT_GAUGE } from '../engine/thread';
import { DEFAULT_SETTINGS, allProgress, clearGeminiKey, type Settings } from '../state/storage';
import { hrefFor } from './router';

interface Props {
  settings: Settings;
  onSettings: (s: Settings) => void;
}

export function SettingsScreen({ settings, onSettings }: Props) {
  const [key, setKey] = useState(settings.geminiKey);
  const [revealed, setRevealed] = useState(false);
  const progress = allProgress();

  return (
    <div className="screen">
      <section className="card">
        <h2>Knot gauge</h2>
        <p className="muted small">
          Gauge is how many rows of knots make one centimetre. It is the number every length in this
          app depends on, and it depends on <em>your</em> tension, not on any rule. Published figures
          disagree — anywhere from about 5 to 7 rows per cm. The defaults here are the middle of that
          range.
        </p>
        <p className="note">
          To measure yours: knot 20 rows of any pattern, lay it flat without stretching, measure it,
          and divide 20 by the length in cm. Do this once and every cut length in Knotwright gets
          better.
        </p>

        <label className="field field--inline">
          <span className="field-label">Chevron / stripe rows per cm</span>
          <input
            type="number"
            min={3}
            max={12}
            step={0.1}
            value={settings.gauge.normalRowsPerCm}
            onChange={(e) =>
              onSettings({
                ...settings,
                gauge: { ...settings.gauge, normalRowsPerCm: clamp(Number(e.target.value), 3, 12) },
              })
            }
          />
        </label>
        <label className="field field--inline">
          <span className="field-label">Alpha rows per cm</span>
          <input
            type="number"
            min={3}
            max={14}
            step={0.1}
            value={settings.gauge.alphaRowsPerCm}
            onChange={(e) =>
              onSettings({
                ...settings,
                gauge: { ...settings.gauge, alphaRowsPerCm: clamp(Number(e.target.value), 3, 14) },
              })
            }
          />
        </label>
        <label className="field field--inline">
          <span className="field-label">Width per string (mm)</span>
          <input
            type="number"
            min={0.5}
            max={4}
            step={0.1}
            value={settings.gauge.mmPerString}
            onChange={(e) =>
              onSettings({
                ...settings,
                gauge: { ...settings.gauge, mmPerString: clamp(Number(e.target.value), 0.5, 4) },
              })
            }
          />
        </label>
        <label className="field field--inline">
          <span className="field-label">Also show inches</span>
          <input
            type="checkbox"
            checked={settings.showInches}
            onChange={(e) => onSettings({ ...settings, showInches: e.target.checked })}
          />
        </label>
        <button
          type="button"
          className="secondary"
          onClick={() => onSettings({ ...settings, gauge: { ...DEFAULT_GAUGE } })}
        >
          Reset gauge to defaults
        </button>
      </section>

      <section className="card">
        <h2>Google Gemini key (optional)</h2>
        <p>
          Knotwright works completely without this. With no key it makes <strong>no network calls at
          all</strong>: colours come from a word list and from your photos, both worked out on this
          device.
        </p>
        <p className="muted small">
          If you add your own free-tier key, it is used for exactly one thing: turning your words or
          photo into a colour scheme and a mood. It is never asked to design knots — a language model
          cannot count rows reliably, and a wrong chart costs you an evening and a pile of thread.
          All the knots, counts and lengths are worked out by code that has tests.
        </p>
        <p className="muted small">
          The key is stored only in this browser and is sent only to Google, straight from this page.
          Knotwright has no server to send it to.
        </p>

        <label className="field">
          <span className="field-label">API key</span>
          <input
            type={revealed ? 'text' : 'password'}
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="AIza…"
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <div className="row">
          <button type="button" className="primary" onClick={() => onSettings({ ...settings, geminiKey: key.trim() })}>
            Save key
          </button>
          <button type="button" className="secondary" onClick={() => setRevealed((r) => !r)}>
            {revealed ? 'Hide' : 'Show'}
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setKey('');
              onSettings(clearGeminiKey());
            }}
          >
            Forget key
          </button>
        </div>
        <label className="field field--inline">
          <span className="field-label">Model</span>
          <input
            value={settings.geminiModel}
            onChange={(e) => onSettings({ ...settings, geminiModel: e.target.value.trim() })}
            spellCheck={false}
          />
        </label>
        <p className="muted small">
          Get a free key at <code>aistudio.google.com/apikey</code>. If the model name above stops
          working, replace it with a current one — Knotwright does not hard-code a model it cannot
          let you change.
        </p>
      </section>

      {progress.length > 0 && (
        <section className="card">
          <h2>Bracelets in progress</h2>
          <ul className="progress-list">
            {progress.map((p) => (
              <li key={p.id}>
                <span>{p.name}</span>
                <b>
                  {p.done} / {p.totalSteps}
                </b>
                <em>{new Date(p.updatedAt).toLocaleDateString()}</em>
              </li>
            ))}
          </ul>
          <p className="muted small">
            Open a bracelet from its link to carry on. Progress is stored per design, in this browser
            only.
          </p>
        </section>
      )}

      <section className="card card--tight">
        <button
          type="button"
          className="secondary"
          onClick={() => onSettings({ ...DEFAULT_SETTINGS, geminiKey: settings.geminiKey })}
        >
          Reset all settings (keeps the key)
        </button>
        <a className="link" href={hrefFor({ name: 'help' })}>
          How Knotwright works, and what it cannot promise →
        </a>
      </section>
    </div>
  );
}

function clamp(n: number, lo: number, hi: number): number {
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : lo;
}
